import React, { memo, useMemo } from 'react';
import {
  View,
  Image,
  StyleSheet,
  StyleProp,
  ViewStyle,
} from 'react-native';
import { Text } from '@/components/common/text';
import { REDESIGN_TYPOGRAPHY } from '@/constants/residents/homeConstants';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { useFontScale } from '@/hooks/shared/useFontScale';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { MarketplaceListing, getConditionDisplayText } from '@/types/residents/marketplaceListing';
import { Hoverable } from '@/components/shared/redesign/Hoverable';

interface MarketplaceListingCardProps {
  listing: MarketplaceListing;
  onPress: () => void;
  style?: StyleProp<ViewStyle>;
}

// The 1x height of the details area — a floor, so a two-line title at large
// text grows the card (and its grid row) instead of clipping the price.
const DETAILS_HEIGHT = 84.5;
const PropertyPlaceholder = RESIDENTS_SVG_COMPONENTS.PROPERTY_PLACEHOLDER;

export function formatTimeAgo(dateString: string): string {
  const now = Date.now();
  const then = new Date(dateString).getTime();
  const diffMs = now - then;
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return 'now';
  if (diffMins < 60) return `${diffMins}m ago`;

  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 30) return `${diffDays}d ago`;

  const diffMonths = Math.floor(diffDays / 30);
  return `${diffMonths}mo ago`;
}

export const MarketplaceListingCard: React.FC<MarketplaceListingCardProps> = memo(({
  listing,
  onPress,
  style,
}) => {
  const { colors } = useThemeColors();
  // The grid gives every card in a row the same height, so the title wraps
  // only at large text; at 1x it ellipsizes exactly as before.
  const { isLargeText } = useFontScale();
  const firstImageUrl = listing.images?.[0]?.url;
  const conditionText = getConditionDisplayText(listing.condition);
  const timeAgo = useMemo(() => formatTimeAgo(listing.createdAt), [listing.createdAt]);

  return (
    // The shared `card` hover — border token, shadow, 1px lift — in place of
    // the old hand-rolled white border and 1.02 scale.
    <Hoverable
      hover="card"
      style={[styles.container, { backgroundColor: colors.card.bg, borderColor: colors.card.border }, style]}
      onPress={onPress}
    >
      <View style={styles.imageArea}>
        {firstImageUrl ? (
          <Image
            source={{ uri: firstImageUrl }}
            style={styles.image}
            resizeMode="cover"
          />
        ) : (
          <View style={[styles.placeholder, { backgroundColor: colors.card.bg }]}>
            <PropertyPlaceholder width={80} height={80} />
          </View>
        )}
      </View>

      <View style={styles.details}>
        <Text style={[styles.title, { color: colors.text.primary }]} numberOfLines={isLargeText ? 2 : 1}>
          {listing.title}
        </Text>
        <Text style={[styles.price, { color: colors.text.muted }]} numberOfLines={1}>
          {listing.price}
        </Text>
        <View style={styles.metaRow}>
          <View style={[styles.conditionBadge, { backgroundColor: colors.accent.greenLight }]}>
            <Text style={[styles.conditionText, { color: colors.accent.green }]}>{conditionText}</Text>
          </View>
          <Text style={[styles.timeAgo, { color: colors.text.dimmer }]}>{timeAgo}</Text>
        </View>
      </View>
    </Hoverable>
  );
});

MarketplaceListingCard.displayName = 'MarketplaceListingCard';

const styles = StyleSheet.create({
  container: {
    borderWidth: 1,
    borderRadius: 16,
    overflow: 'hidden',
    padding: 1,
  },
  imageArea: {
    flex: 1,
    minHeight: 120,
  },
  image: {
    width: '100%',
    height: '100%',
  },
  placeholder: {
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  details: {
    minHeight: DETAILS_HEIGHT,
    paddingHorizontal: 14,
    paddingTop: 12,
  },
  price: {
    ...REDESIGN_TYPOGRAPHY.listingTitle,
    marginTop: 4,
  },
  title: {
    ...REDESIGN_TYPOGRAPHY.listingPrice,
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 7.5,
  },
  conditionBadge: {
    borderRadius: 4,
    paddingHorizontal: 7,
    paddingVertical: 2,
  },
  conditionText: {
    ...REDESIGN_TYPOGRAPHY.conditionBadge,
  },
  timeAgo: {
    ...REDESIGN_TYPOGRAPHY.timestamp,
  },
});
